import { useRef, useState } from 'react';

import {
  GetClientDto,
  ResponseError,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { responseErrors } from '../../../common/pokCore/validation/responseErrors';

export type ImportAlertVariant = 'info' | 'warning' | 'success' | 'danger';

const findFileByName = (list: FileList, targetFileName: string) => {
  for (let i = 0; i < list.length; i++) {
    if (list[i].name.trim() === String(targetFileName).trim()) {
      return list[i];
    }
  }
  return null;
};

export const usePurchaseInvoiceImport = <
  TRow extends { invoiceNumber: string },
>(
  prepareRows: (fileContent: string, clientId: string) => Promise<TRow[]>,
  processRow: (
    row: TRow,
    file: File,
    clientId: string,
    companyId: string,
  ) => Promise<void>,
) => {
  const pok = usePokCore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileNameRef = useRef<HTMLInputElement>(null);
  const directoryInputRef = useRef<HTMLInputElement>(null);
  const directoryNameRef = useRef<HTMLInputElement>(null);
  const [showNewAlert, setShowNewAlert] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [variant, setVariant] = useState<ImportAlertVariant>('success');
  const [waiting, setWaiting] = useState(false);
  const [alert, setAlert] = useState<string[]>([]);
  const [xlsxFile, setXlsxFile] = useState<File | null>(null);
  const [excel, setExcel] = useState('');
  const [directory, setDirectory] = useState('');
  const [client, setClient] = useState<GetClientDto>();
  const [fileList, setFileList] = useState<FileList | null>(null);
  const { t, tk } = useTranslation('fvzLng');

  const handleFileChange = (files: FileList | null) => {
    if (!files || files.length === 0) {
      return;
    }
    setXlsxFile(files[0]);
    setExcel(files[0].name);
  };

  const handleDirectoryChange = (files: FileList | null) => {
    if (!files || files.length === 0) {
      return;
    }
    const directoryName = files[0].webkitRelativePath
      ? files[0].webkitRelativePath.split('/')[0]
      : '';
    setDirectory(directoryName);
    setFileList(files);
  };

  const handleSearch = async () => {
    if (!xlsxFile || !fileList || !client) {
      setShowNewAlert(true);
      return;
    }
    setShowNewAlert(false);
    setAlert([t(tk.finance.dataDownload)]);
    setVariant('info');
    setShowAlert(true);
    setWaiting(true);

    let rows: TRow[];
    try {
      const buffer = await xlsxFile.arrayBuffer();
      const base64 = btoa(
        new Uint8Array(buffer).reduce((d, b) => d + String.fromCharCode(b), ''),
      );
      rows = await prepareRows(base64, client.id);
    } catch (response) {
      setVariant('danger');
      setWaiting(false);
      setAlert(await responseErrors(response as ResponseError));
      return;
    }

    const errors: string[] = [];
    const companyId = pok.getCompanyId();
    let successCount = 0;

    for (const row of rows) {
      const rowWithError = row as typeof row & {
        validationError?: string | null;
      };
      if (rowWithError.validationError) {
        errors.push(
          `${t(tk.finance.invoice)} ${row.invoiceNumber} - ${rowWithError.validationError}`,
        );
        continue;
      }
      const file = findFileByName(fileList, `${row.invoiceNumber}.pdf`);
      if (file !== null) {
        try {
          await processRow(row, file, client.id, companyId);
          successCount++;
        } catch (response) {
          errors.push(
            `${t(tk.finance.invoice)} ${row.invoiceNumber} - ${await responseErrors(response as ResponseError)}`,
          );
        }
      } else {
        errors.push(
          `${t(tk.finance.invoice)} ${row.invoiceNumber} - ${t(tk.finance.fileNotFound)} ${row.invoiceNumber}.pdf`,
        );
      }
    }

    const summaryVariant =
      errors.length === 0 ? 'success' : successCount > 0 ? 'warning' : 'danger';
    const summaryMessages =
      errors.length === 0
        ? [
            t(tk.finance.finishDownload),
            t(tk.finance.importSuccessCount, {
              success: successCount,
              total: rows.length,
            }),
          ]
        : [
            t(tk.finance.importSuccessCount, {
              success: successCount,
              total: rows.length,
            }),
            t(tk.finance.importProblematicInvoices),
            ...errors,
          ];
    setVariant(summaryVariant);
    setShowNewAlert(false);
    setWaiting(false);
    setAlert(summaryMessages);
    setXlsxFile(null);
    setExcel('');
  };

  return {
    fileInputRef,
    fileNameRef,
    directoryInputRef,
    directoryNameRef,
    showNewAlert,
    showAlert,
    variant,
    waiting,
    alert,
    excel,
    directory,
    client,
    setClient,
    handleFileChange,
    handleDirectoryChange,
    handleSearch,
  };
};
